import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { studioLogin, studioAuth } from "../lib/studioApi";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import PricingSection from "../components/studio/PricingSection";

// Honor ?next=<path> so RequireStudioAccess can bounce users back to the page
// they were trying to reach (e.g. /chords) after login. Defaults to /studio
// for the standard "I came here directly" case. Only same-origin paths are
// honored so the query param can't be weaponized into an open redirect.
function resolveNextPath(search) {
  const raw = new URLSearchParams(search).get("next") || "";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/studio";
  return raw;
}

export default function StudioLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = resolveNextPath(location.search);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // If already logged in, redirect
  React.useEffect(() => {
    if (studioAuth.getToken()) navigate(nextPath, { replace: true });
  }, [navigate, nextPath]);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password required");
      return;
    }
    setLoading(true);
    try {
      await studioLogin(email, password);
      toast.success("Welcome back");
      navigate(nextPath, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0f1e] text-white flex flex-col items-center px-6 py-12 md:py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <BrandLogo variant="yellow" className="h-10 mx-auto mb-2" />
          <div className="text-xs uppercase tracking-[0.3em] text-white/40">Private Studio</div>
        </div>

        <form onSubmit={submit} className="bg-[#10162a] border border-white/5 rounded-2xl p-7 shadow-[0_10px_40px_rgba(0,0,0,0.4)]">
          <h1 className="text-2xl font-bold mb-1">Sign in</h1>
          <p className="text-sm text-white/50 mb-6">Enter your private studio</p>

          <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">Email</label>
          <Input
            data-testid="studio-login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 mb-4 bg-[#0b0f1e] border-white/10 text-white"
            autoFocus
            disabled={loading}
          />

          <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">Password</label>
          <Input
            data-testid="studio-login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-12 mb-6 bg-[#0b0f1e] border-white/10 text-white"
            disabled={loading}
          />

          <Button
            data-testid="studio-login-submit"
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-paul-yellow hover:bg-paul-yellow text-navy font-bold"
            style={{ color: "#0b1535" }}
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
          </Button>

          <p className="mt-6 text-xs text-white/40 text-center">
            New here? Pick a plan below to create your account.
          </p>
        </form>
      </div>

      {/* Decorative divider */}
      <div className="w-full max-w-md flex items-center gap-4 my-12">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">or</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      {/* Pricing / Join the Studio */}
      <PricingSection />
    </div>
  );
}
